import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { hostApi, waitlistApi, reservationsApi, tablesApi, sectionsApi, shiftInstancesApi, type WaitlistEntry, type ShiftInstance } from '../services/api';
import {
  Users,
  Clock,
  MapPin,
  Phone,
  MessageSquare,
  Check,
  X,
  Plus,
  ChevronRight,
  AlertTriangle,
  Calendar,
  UserPlus,
  Armchair,
  Timer,
  RefreshCw,
  RotateCcw,
  Loader2,
  Lightbulb,
  ArrowRight,
  TrendingUp,
} from 'lucide-react';
import HelpButton from '../components/HelpButton';
import ShiftTimelineBar from '../components/host/ShiftTimelineBar';
import { format, parseISO, differenceInMinutes, isToday } from 'date-fns';
import clsx from 'clsx';

// Types
interface Table {
  id: string;
  number: string;
  name?: string;
  capacity: number;
  status: 'available' | 'seated' | 'occupied' | 'reserved' | 'blocked' | 'needs_cleaning' | 'check_dropped';
  sectionId?: string;
  currentCheckId?: string;
  positionX: number;
  positionY: number;
  shape: string;
  width: number;
  height: number;
}

interface Section {
  id: string;
  name: string;
  color?: string;
}

interface Reservation {
  id: string;
  guestName: string;
  guestPhone?: string;
  partySize: number;
  date: string;
  time: string;
  status: string;
  checkedInAt?: string;
  seatedAt?: string;
  specialRequests?: string;
  notes?: string;
}

interface FloorTableData extends Table {
  check?: {
    id: string;
    partySize: number;
    guestName?: string;
    createdAt: string;
    server?: { firstName: string; lastName: string };
    course?: string;
  };
  dwellMinutes?: number;
}

type TabType = 'pre-service' | 'live-floor' | 'waitlist';

export default function HostPage() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabType>('live-floor');
  const [showSeatModal, setShowSeatModal] = useState(false);
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);
  const [selectedTable, setSelectedTable] = useState<Table | null>(null);
  const [selectedReservation, setSelectedReservation] = useState<Reservation | null>(null);

  // Fetch overview data
  const { data: overviewData, isLoading: overviewLoading, refetch: refetchOverview } = useQuery({
    queryKey: ['host-overview'],
    queryFn: async () => {
      const response = await hostApi.getOverview();
      return response.data?.data || response.data;
    },
    refetchInterval: 30000,
  });

  // Fetch waitlist
  const { data: waitlistData, isLoading: waitlistLoading } = useQuery({
    queryKey: ['waitlist'],
    queryFn: async () => {
      const response = await waitlistApi.getAll();
      return response.data?.data || response.data || [];
    },
    refetchInterval: 15000,
  });

  // Fetch sections
  const { data: sectionsData } = useQuery({
    queryKey: ['sections'],
    queryFn: async () => {
      const response = await sectionsApi.getAll();
      return response.data?.data || response.data || [];
    },
  });

  // Fetch today's reservations
  const { data: reservationsData } = useQuery({
    queryKey: ['reservations', 'today'],
    queryFn: async () => {
      const today = format(new Date(), 'yyyy-MM-dd');
      const response = await reservationsApi.getAll({ date: today });
      return response.data?.data || response.data || [];
    },
    refetchInterval: 30000,
  });

  // Fetch optimization suggestions
  const { data: optimizationData, isLoading: optimizationLoading } = useQuery({
    queryKey: ['optimization-suggestions'],
    queryFn: async () => {
      const response = await hostApi.getOptimizationSuggestions();
      return response.data?.data || response.data;
    },
    refetchInterval: 60000, // Refresh every minute
  });

  // Fetch cover forecast for timeline
  const { data: forecastData } = useQuery({
    queryKey: ['cover-forecast'],
    queryFn: async () => {
      const response = await hostApi.getCoverForecast();
      return response.data?.data || response.data;
    },
    refetchInterval: 300000, // Refresh every 5 minutes
  });

  // Fetch today's shift instances to find current/active shift
  const { data: todayShiftsData } = useQuery({
    queryKey: ['shift-instances', 'today'],
    queryFn: async () => {
      const today = format(new Date(), 'yyyy-MM-dd');
      const response = await shiftInstancesApi.getByDateRange(today, today);
      return response.data?.data || response.data || [];
    },
    refetchInterval: 60000, // Refresh every minute
  });

  // Find the current active shift or the next upcoming shift
  const currentShift = useMemo(() => {
    const shifts: ShiftInstance[] = todayShiftsData || [];
    if (shifts.length === 0) return null;

    const now = new Date();

    // First, look for an in_progress shift
    const inProgress = shifts.find(s => s.status === 'in_progress');
    if (inProgress) return inProgress;

    // Next, find shifts that overlap with current time
    const currentlyActive = shifts.find(s => {
      const start = new Date(s.startTime);
      const end = new Date(s.endTime);
      return now >= start && now <= end;
    });
    if (currentlyActive) return currentlyActive;

    // Otherwise, find the next upcoming shift today
    const upcoming = shifts
      .filter(s => new Date(s.startTime) > now && s.status !== 'cancelled')
      .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
    if (upcoming.length > 0) return upcoming[0];

    // Fallback to the most recent shift that already ended
    const past = shifts
      .filter(s => new Date(s.endTime) < now && s.status !== 'cancelled')
      .sort((a, b) => new Date(b.endTime).getTime() - new Date(a.endTime).getTime());
    if (past.length > 0) return past[0];

    // Last resort: just return the first shift
    return shifts[0];
  }, [todayShiftsData]);

  const tables: FloorTableData[] = overviewData?.tables || [];
  const waitlist: WaitlistEntry[] = Array.isArray(waitlistData) ? waitlistData : [];
  const sections: Section[] = sectionsData || [];
  const reservations: Reservation[] = Array.isArray(reservationsData) ? reservationsData : [];

  // Filter reservations
  const upcomingReservations = reservations.filter(r =>
    ['pending', 'confirmed'].includes(r.status) && !r.seatedAt
  );
  const checkedInReservations = reservations.filter(r => r.checkedInAt && !r.seatedAt);

  // Table stats
  const tableStats = {
    available: tables.filter(t => t.status === 'available').length,
    occupied: tables.filter(t => ['seated', 'occupied'].includes(t.status)).length,
    reserved: tables.filter(t => t.status === 'reserved').length,
    total: tables.length,
  };

  // Mutations - with comprehensive invalidation for cross-view consistency (Host, POS, Reservations)
  const seatWalkInMutation = useMutation({
    mutationFn: (data: { tableId: string; partySize: number; guestName?: string; guestPhone?: string }) =>
      hostApi.seatWalkIn(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['host-overview'] });
      queryClient.invalidateQueries({ queryKey: ['tables'] });
      queryClient.invalidateQueries({ queryKey: ['sections'] }); // POS view
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
      queryClient.invalidateQueries({ queryKey: ['guests'] });
      setShowSeatModal(false);
      setSelectedTable(null);
    },
  });

  const addToWaitlistMutation = useMutation({
    mutationFn: (data: { guestName: string; partySize: number; guestPhone?: string; quotedWaitMinutes?: number }) =>
      waitlistApi.add(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['waitlist'] });
      queryClient.invalidateQueries({ queryKey: ['host-overview'] });
      setShowWaitlistModal(false);
    },
  });

  const textWaitlistMutation = useMutation({
    mutationFn: (id: string) => waitlistApi.sendText(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['waitlist'] });
    },
  });

  const seatFromWaitlistMutation = useMutation({
    mutationFn: ({ entryId, tableId }: { entryId: string; tableId: string }) =>
      waitlistApi.seat(entryId, tableId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['waitlist'] });
      queryClient.invalidateQueries({ queryKey: ['host-overview'] });
      queryClient.invalidateQueries({ queryKey: ['tables'] });
      queryClient.invalidateQueries({ queryKey: ['sections'] }); // POS view
      queryClient.invalidateQueries({ queryKey: ['guests'] });
    },
  });

  const checkInMutation = useMutation({
    mutationFn: (reservationId: string) => hostApi.checkInReservation(reservationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
      queryClient.invalidateQueries({ queryKey: ['host-overview'] });
    },
  });

  const clearTableMutation = useMutation({
    mutationFn: (tableId: string) => tablesApi.updateStatus(tableId, 'available'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['host-overview'] });
      queryClient.invalidateQueries({ queryKey: ['tables'] });
      queryClient.invalidateQueries({ queryKey: ['sections'] }); // POS view
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
    },
  });

  const blockTableMutation = useMutation({
    mutationFn: ({ tableId, blocked, reason }: { tableId: string; blocked: boolean; reason?: string }) =>
      hostApi.blockTable(tableId, { blocked, reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['host-overview'] });
      queryClient.invalidateQueries({ queryKey: ['tables'] });
      queryClient.invalidateQueries({ queryKey: ['sections'] }); // POS view
    },
  });

  // Status colors (using Tailwind classes consistent with app)
  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'available':
        return { bg: 'bg-success-50', border: 'border-success-200', dot: 'bg-success-500', text: 'text-success-700' };
      case 'seated':
      case 'occupied':
        return { bg: 'bg-danger-50', border: 'border-danger-200', dot: 'bg-danger-500', text: 'text-danger-700' };
      case 'reserved':
        return { bg: 'bg-primary-50', border: 'border-primary-200', dot: 'bg-primary-500', text: 'text-primary-700' };
      case 'blocked':
        return { bg: 'bg-slate-100', border: 'border-slate-300', dot: 'bg-slate-500', text: 'text-slate-600' };
      case 'needs_cleaning':
        return { bg: 'bg-warning-50', border: 'border-warning-200', dot: 'bg-warning-500', text: 'text-warning-700' };
      case 'check_dropped':
        return { bg: 'bg-orange-50', border: 'border-orange-200', dot: 'bg-orange-500', text: 'text-orange-700' };
      default:
        return { bg: 'bg-slate-50', border: 'border-slate-200', dot: 'bg-slate-400', text: 'text-slate-600' };
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-slate-900">Host Station</h1>
            <p className="text-sm text-slate-500">
              {format(new Date(), 'EEEE, MMMM d')} &middot; Manage seating and reservations
            </p>
          </div>
          <HelpButton featureId="host-station" />
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => refetchOverview()}
            className="btn btn-secondary flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-success-100 rounded-lg">
              <Armchair className="w-5 h-5 text-success-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{tableStats.available}</p>
              <p className="text-sm text-slate-500">Available</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-danger-100 rounded-lg">
              <Users className="w-5 h-5 text-danger-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{tableStats.occupied}</p>
              <p className="text-sm text-slate-500">Occupied</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-primary-100 rounded-lg">
              <Calendar className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{upcomingReservations.length}</p>
              <p className="text-sm text-slate-500">Upcoming</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-purple-100 rounded-lg">
              <Timer className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{waitlist.length}</p>
              <p className="text-sm text-slate-500">Waitlist</p>
            </div>
          </div>
        </div>
      </div>

      {/* Shift Timeline Bar */}
      {currentShift && (
        <ShiftTimelineBar
          shiftName={currentShift.name}
          shiftStart={currentShift.startTime}
          shiftEnd={currentShift.endTime}
          shiftStatus={currentShift.status}
          reservations={reservations}
          hourlyForecast={forecastData?.hourlyForecast || []}
          peakHour={forecastData?.summary?.peakHour}
          waitlistCount={waitlist.length}
        />
      )}

      {/* Tabs */}
      <div className="border-b border-slate-200">
        <nav className="flex gap-6">
          {[
            { id: 'pre-service', label: 'Pre-Service', icon: Calendar },
            { id: 'live-floor', label: 'Live Floor', icon: MapPin },
            { id: 'waitlist', label: 'Waitlist', icon: Timer, badge: waitlist.length },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as TabType)}
              className={clsx(
                'flex items-center gap-2 py-3 px-1 border-b-2 text-sm font-medium transition-colors',
                activeTab === tab.id
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              )}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.badge ? (
                <span className="ml-1.5 px-2 py-0.5 text-xs rounded-full bg-purple-100 text-purple-700">
                  {tab.badge}
                </span>
              ) : null}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="min-h-[400px]">
        {/* Pre-Service Tab */}
        {activeTab === 'pre-service' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Today's Reservations */}
            <div className="card">
              <div className="px-5 py-4 border-b border-slate-200">
                <h3 className="font-semibold text-slate-900">Today's Reservations</h3>
                <p className="text-sm text-slate-500">{reservations.length} total</p>
              </div>
              <div className="divide-y divide-slate-100 max-h-[400px] overflow-y-auto">
                {upcomingReservations.length === 0 ? (
                  <div className="p-8 text-center text-slate-400">
                    <Calendar className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p>No upcoming reservations</p>
                  </div>
                ) : (
                  upcomingReservations.map(res => (
                    <div key={res.id} className="px-5 py-4 hover:bg-slate-50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={clsx(
                            'w-2 h-2 rounded-full',
                            res.status === 'confirmed' ? 'bg-success-500' : 'bg-warning-500'
                          )} />
                          <div>
                            <p className="font-medium text-slate-900">{res.guestName}</p>
                            <div className="flex items-center gap-3 text-sm text-slate-500">
                              <span className="flex items-center gap-1">
                                <Clock className="w-3.5 h-3.5" />
                                {res.time}
                              </span>
                              <span className="flex items-center gap-1">
                                <Users className="w-3.5 h-3.5" />
                                {res.partySize}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div>
                          {!res.checkedInAt ? (
                            <button
                              onClick={() => checkInMutation.mutate(res.id)}
                              disabled={checkInMutation.isPending}
                              className="btn btn-secondary text-sm"
                            >
                              {checkInMutation.isPending ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                'Check In'
                              )}
                            </button>
                          ) : (
                            <span className="badge badge-success">Checked In</span>
                          )}
                        </div>
                      </div>
                      {res.specialRequests && (
                        <p className="mt-2 text-sm text-slate-500 pl-5">
                          {res.specialRequests}
                        </p>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Checked In - Waiting to Seat */}
            <div className="card">
              <div className="px-5 py-4 border-b border-slate-200">
                <h3 className="font-semibold text-slate-900">Ready to Seat</h3>
                <p className="text-sm text-slate-500">{checkedInReservations.length} waiting</p>
              </div>
              <div className="divide-y divide-slate-100 max-h-[400px] overflow-y-auto">
                {checkedInReservations.length === 0 ? (
                  <div className="p-8 text-center text-slate-400">
                    <Users className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p>No guests waiting to be seated</p>
                  </div>
                ) : (
                  checkedInReservations.map(res => (
                    <div key={res.id} className="px-5 py-4 hover:bg-slate-50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-slate-900">{res.guestName}</p>
                          <div className="flex items-center gap-3 text-sm text-slate-500">
                            <span className="flex items-center gap-1">
                              <Users className="w-3.5 h-3.5" />
                              Party of {res.partySize}
                            </span>
                            {res.checkedInAt && (
                              <span className="flex items-center gap-1 text-warning-600">
                                <Clock className="w-3.5 h-3.5" />
                                {differenceInMinutes(new Date(), parseISO(res.checkedInAt))}m wait
                              </span>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            setSelectedReservation(res);
                            setShowSeatModal(true);
                          }}
                          className="btn btn-primary text-sm"
                        >
                          Seat
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Action Items */}
            <div className="card lg:col-span-2">
              <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Lightbulb className="w-5 h-5 text-primary-500" />
                  <h3 className="font-semibold text-slate-900">Action Items</h3>
                </div>
                {optimizationData?.suggestions?.length > 0 && (
                  <span className="text-xs font-medium text-primary-600 bg-primary-50 px-2 py-1 rounded-full">
                    {optimizationData.suggestions.length} actions
                  </span>
                )}
              </div>
              <div className="divide-y divide-slate-100">
                {optimizationLoading ? (
                  <div className="py-8 text-center">
                    <Loader2 className="w-6 h-6 animate-spin text-primary-500 mx-auto" />
                  </div>
                ) : optimizationData?.suggestions && optimizationData.suggestions.length > 0 ? (
                  optimizationData.suggestions.map((suggestion: any, idx: number) => (
                    <div
                      key={idx}
                      className="p-4 flex items-center gap-4 hover:bg-slate-50 transition-colors"
                    >
                      {/* Priority indicator */}
                      <div className={clsx(
                        'w-2 h-2 rounded-full flex-shrink-0',
                        suggestion.priority === 'high' ? 'bg-danger-500' :
                        suggestion.priority === 'medium' ? 'bg-warning-500' :
                        'bg-slate-400'
                      )} />

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 text-sm truncate">{suggestion.title}</p>
                        <p className="text-xs text-slate-500 truncate">{suggestion.description}</p>
                      </div>

                      {/* Impact badge */}
                      <span className="text-xs text-success-600 bg-success-50 px-2 py-1 rounded-full whitespace-nowrap flex-shrink-0">
                        {suggestion.impact}
                      </span>

                      {/* Action button */}
                      <button
                        onClick={() => {
                          // Handle the action based on type
                          if (suggestion.action?.type === 'update_table_status') {
                            tablesApi.updateStatus(
                              suggestion.action.details.tableId,
                              suggestion.action.details.newStatus
                            ).then(() => {
                              queryClient.invalidateQueries({ queryKey: ['tables'] });
                              queryClient.invalidateQueries({ queryKey: ['optimization-suggestions'] });
                            });
                          } else if (suggestion.action?.type === 'seat_from_waitlist') {
                            waitlistApi.seat(
                              suggestion.action.details.entryId,
                              suggestion.action.details.tableId
                            ).then(() => {
                              queryClient.invalidateQueries({ queryKey: ['waitlist'] });
                              queryClient.invalidateQueries({ queryKey: ['tables'] });
                              queryClient.invalidateQueries({ queryKey: ['optimization-suggestions'] });
                            });
                          } else if (suggestion.action?.type === 'text_waitlist_guest') {
                            waitlistApi.sendText(suggestion.action.details.entryId).then(() => {
                              queryClient.invalidateQueries({ queryKey: ['waitlist'] });
                              queryClient.invalidateQueries({ queryKey: ['optimization-suggestions'] });
                            });
                          } else if (suggestion.action?.type === 'confirm_reservation') {
                            reservationsApi.confirm(
                              suggestion.action.details.reservationId
                            ).then(() => {
                              queryClient.invalidateQueries({ queryKey: ['reservations'] });
                              queryClient.invalidateQueries({ queryKey: ['optimization-suggestions'] });
                            });
                          } else if (suggestion.action?.type === 'handle_noshow' && suggestion.action.details.guestPhone) {
                            window.location.href = `tel:${suggestion.action.details.guestPhone}`;
                          }
                        }}
                        className={clsx(
                          'px-3 py-1.5 text-xs font-medium rounded-lg transition-colors flex-shrink-0',
                          suggestion.priority === 'high'
                            ? 'bg-danger-500 text-white hover:bg-danger-600'
                            : 'bg-primary-500 text-white hover:bg-primary-600'
                        )}
                      >
                        {suggestion.actionLabel || 'Action'}
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="py-12 text-center">
                    <Check className="w-12 h-12 mx-auto mb-3 text-success-500" />
                    <p className="font-medium text-slate-900">All caught up!</p>
                    <p className="text-sm text-slate-500 mt-1">No actions needed right now</p>
                  </div>
                )}
              </div>

              {/* Compact Occupancy Bar - only show when there's activity */}
              {optimizationData?.currentOccupancy && optimizationData.currentOccupancy.seatedCovers > 0 && (
                <div className="px-4 py-3 bg-slate-50 border-t border-slate-200">
                  <div className="flex items-center justify-between text-xs mb-2">
                    <span className="text-slate-500">Current Occupancy</span>
                    <span className="font-medium text-slate-900">
                      {optimizationData.currentOccupancy.seatedCovers} / {optimizationData.currentOccupancy.totalCapacity} seats
                      ({optimizationData.currentOccupancy.occupancyRate}%)
                    </span>
                  </div>
                  <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div
                      className={clsx(
                        'h-full rounded-full transition-all',
                        optimizationData.currentOccupancy.occupancyRate > 80 ? 'bg-danger-500' :
                        optimizationData.currentOccupancy.occupancyRate > 50 ? 'bg-warning-500' :
                        'bg-success-500'
                      )}
                      style={{ width: `${optimizationData.currentOccupancy.occupancyRate}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Live Floor Tab */}
        {activeTab === 'live-floor' && (
          <div className="space-y-5">
            {/* Quick Actions */}
            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => setShowSeatModal(true)}
                className="btn btn-primary flex items-center gap-2"
              >
                <UserPlus className="w-4 h-4" />
                Seat Walk-In
              </button>
              <button
                onClick={() => setShowWaitlistModal(true)}
                className="btn btn-secondary flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add to Waitlist
              </button>
            </div>

            {/* Table Legend */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600">
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full bg-success-500" />
                Available
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full bg-danger-500" />
                Occupied
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full bg-primary-500" />
                Reserved
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full bg-warning-500" />
                Needs Cleaning
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full bg-slate-400" />
                Blocked
              </span>
            </div>

            {/* Floor Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {tables.map(table => {
                const config = getStatusConfig(table.status);
                return (
                  <div
                    key={table.id}
                    onClick={() => {
                      if (table.status === 'available') {
                        setSelectedTable(table);
                        setShowSeatModal(true);
                      }
                    }}
                    className={clsx(
                      'relative card p-4 cursor-pointer transition-all hover:shadow-md border-2',
                      config.bg,
                      config.border,
                      table.status === 'available' && 'hover:border-success-400 hover:scale-[1.02]'
                    )}
                  >
                    {/* Status indicator */}
                    <div className={clsx(
                      'absolute top-3 right-3 w-2.5 h-2.5 rounded-full',
                      config.dot
                    )} />

                    {/* Table info */}
                    <div className="text-center">
                      <p className="text-xl font-bold text-slate-900">{table.number}</p>
                      <p className="text-xs text-slate-500">{table.capacity} seats</p>
                    </div>

                    {/* Occupied info */}
                    {table.check && (
                      <div className="mt-3 pt-3 border-t border-slate-200 text-xs">
                        <p className="font-medium text-slate-700 truncate">
                          {table.check.guestName || `Party of ${table.check.partySize}`}
                        </p>
                        {table.dwellMinutes !== undefined && (
                          <p className={clsx(
                            'mt-0.5',
                            table.dwellMinutes > 90 ? 'text-warning-600' : 'text-slate-500'
                          )}>
                            {table.dwellMinutes}m
                          </p>
                        )}
                      </div>
                    )}

                    {/* Quick action for occupied tables */}
                    {['seated', 'occupied', 'needs_cleaning'].includes(table.status) && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (confirm(`Clear table ${table.number}?`)) {
                            clearTableMutation.mutate(table.id);
                          }
                        }}
                        className="mt-3 w-full py-1.5 text-xs font-medium bg-white border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50 transition-colors"
                      >
                        Clear Table
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            {tables.length === 0 && !overviewLoading && (
              <div className="card p-12 text-center">
                <MapPin className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No tables configured</p>
                <Link
                  to="/floor-plan"
                  className="inline-block mt-3 btn btn-primary"
                >
                  Set Up Floor Plan
                </Link>
              </div>
            )}

            {overviewLoading && (
              <div className="card p-12 text-center">
                <Loader2 className="w-8 h-8 animate-spin text-primary-500 mx-auto" />
              </div>
            )}
          </div>
        )}

        {/* Waitlist Tab */}
        {activeTab === 'waitlist' && (
          <div className="space-y-5">
            {/* Add to Waitlist Button */}
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-900">Current Waitlist</h3>
              <button
                onClick={() => setShowWaitlistModal(true)}
                className="btn btn-primary flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Party
              </button>
            </div>

            {/* Waitlist Items */}
            <div className="card">
              {waitlist.length === 0 ? (
                <div className="p-12 text-center text-slate-400">
                  <Timer className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p className="font-medium text-slate-600">No one on the waitlist</p>
                  <p className="text-sm mt-1">Add a party when guests arrive without a reservation</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {waitlist.map((entry, index) => (
                    <div key={entry.id} className="px-5 py-4 hover:bg-slate-50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-purple-700 font-bold text-sm">
                            {index + 1}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{entry.guestName}</p>
                            <div className="flex items-center gap-3 text-sm text-slate-500">
                              <span className="flex items-center gap-1">
                                <Users className="w-3.5 h-3.5" />
                                {entry.partySize}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-3.5 h-3.5" />
                                {entry.waitTimeMinutes || 0}m
                              </span>
                              {entry.quotedWaitMinutes && (
                                <span className="text-slate-400">
                                  (quoted {entry.quotedWaitMinutes}m)
                                </span>
                              )}
                              {entry.status === 'texted' && (
                                <span className="badge badge-info">
                                  <MessageSquare className="w-3 h-3 mr-1" />
                                  Texted
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {entry.guestPhone && entry.status !== 'texted' && (
                            <button
                              onClick={() => textWaitlistMutation.mutate(entry.id)}
                              disabled={textWaitlistMutation.isPending}
                              className="btn btn-ghost p-2"
                              title="Send text notification"
                            >
                              <MessageSquare className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => {
                              const availableTable = tables.find(t => t.status === 'available' && t.capacity >= entry.partySize);
                              if (availableTable) {
                                if (confirm(`Seat ${entry.guestName} at table ${availableTable.number}?`)) {
                                  seatFromWaitlistMutation.mutate({ entryId: entry.id, tableId: availableTable.id });
                                }
                              } else {
                                alert('No available tables for this party size');
                              }
                            }}
                            disabled={seatFromWaitlistMutation.isPending}
                            className="btn btn-primary text-sm"
                          >
                            Seat
                          </button>
                          <button
                            onClick={() => {
                              if (confirm(`Remove ${entry.guestName} from waitlist?`)) {
                                waitlistApi.remove(entry.id).then(() => {
                                  queryClient.invalidateQueries({ queryKey: ['waitlist'] });
                                });
                              }
                            }}
                            className="btn btn-ghost p-2 text-slate-400 hover:text-danger-600"
                            title="Remove from waitlist"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      {entry.specialRequests && (
                        <p className="mt-2 text-sm text-slate-500 pl-12">
                          {entry.specialRequests}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Seat Walk-In Modal */}
      {showSeatModal && (
        <SeatModal
          table={selectedTable}
          reservation={selectedReservation}
          tables={tables}
          onClose={() => {
            setShowSeatModal(false);
            setSelectedTable(null);
            setSelectedReservation(null);
          }}
          onSeat={(data) => {
            seatWalkInMutation.mutate(data);
          }}
          isLoading={seatWalkInMutation.isPending}
        />
      )}

      {/* Add to Waitlist Modal */}
      {showWaitlistModal && (
        <WaitlistModal
          onClose={() => setShowWaitlistModal(false)}
          onAdd={(data) => {
            addToWaitlistMutation.mutate(data);
          }}
          isLoading={addToWaitlistMutation.isPending}
        />
      )}
    </div>
  );
}

// Seat Modal Component
function SeatModal({
  table,
  reservation,
  tables,
  onClose,
  onSeat,
  isLoading,
}: {
  table: Table | null;
  reservation: Reservation | null;
  tables: FloorTableData[];
  onClose: () => void;
  onSeat: (data: { tableId: string; partySize: number; guestName?: string; guestPhone?: string }) => void;
  isLoading: boolean;
}) {
  const [selectedTableId, setSelectedTableId] = useState(table?.id || '');
  const [partySize, setPartySize] = useState(reservation?.partySize || 2);
  const [guestName, setGuestName] = useState(reservation?.guestName || '');
  const [guestPhone, setGuestPhone] = useState(reservation?.guestPhone || '');

  const availableTables = tables.filter(t => t.status === 'available');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTableId) return;
    onSeat({
      tableId: selectedTableId,
      partySize,
      guestName: guestName || undefined,
      guestPhone: guestPhone || undefined,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-md shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">
            {reservation ? 'Seat Reservation' : 'Seat Walk-In'}
          </h2>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Table</label>
            <select
              value={selectedTableId}
              onChange={e => setSelectedTableId(e.target.value)}
              className="input"
              required
            >
              <option value="">Select a table</option>
              {availableTables.map(t => (
                <option key={t.id} value={t.id}>
                  Table {t.number} ({t.capacity} seats)
                </option>
              ))}
            </select>
            {availableTables.length === 0 && (
              <p className="text-sm text-warning-600 mt-1">No tables available</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Party Size</label>
            <input
              type="number"
              min={1}
              max={20}
              value={partySize}
              onChange={e => setPartySize(parseInt(e.target.value))}
              className="input"
              required
            />
          </div>
          {!reservation && (
            <>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Guest Name (optional)</label>
                <input
                  type="text"
                  value={guestName}
                  onChange={e => setGuestName(e.target.value)}
                  className="input"
                  placeholder="John Smith"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Phone (optional)</label>
                <input
                  type="tel"
                  value={guestPhone}
                  onChange={e => setGuestPhone(e.target.value)}
                  className="input"
                  placeholder="555-123-4567"
                />
              </div>
            </>
          )}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary flex-1"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!selectedTableId || isLoading}
              className="btn btn-primary flex-1"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Seat Party'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Waitlist Modal Component
function WaitlistModal({
  onClose,
  onAdd,
  isLoading,
}: {
  onClose: () => void;
  onAdd: (data: { guestName: string; partySize: number; guestPhone?: string; quotedWaitMinutes?: number }) => void;
  isLoading: boolean;
}) {
  const [guestName, setGuestName] = useState('');
  const [partySize, setPartySize] = useState(2);
  const [guestPhone, setGuestPhone] = useState('');
  const [quotedWait, setQuotedWait] = useState<number | ''>('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onAdd({
      guestName,
      partySize,
      guestPhone: guestPhone || undefined,
      quotedWaitMinutes: quotedWait || undefined,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-md shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">Add to Waitlist</h2>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Guest Name</label>
            <input
              type="text"
              value={guestName}
              onChange={e => setGuestName(e.target.value)}
              className="input"
              placeholder="John Smith"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Party Size</label>
            <input
              type="number"
              min={1}
              max={20}
              value={partySize}
              onChange={e => setPartySize(parseInt(e.target.value))}
              className="input"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Phone (for text notifications)</label>
            <input
              type="tel"
              value={guestPhone}
              onChange={e => setGuestPhone(e.target.value)}
              className="input"
              placeholder="555-123-4567"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Quoted Wait (minutes)</label>
            <input
              type="number"
              min={5}
              max={180}
              step={5}
              value={quotedWait}
              onChange={e => setQuotedWait(e.target.value ? parseInt(e.target.value) : '')}
              className="input"
              placeholder="15"
            />
          </div>
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary flex-1"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!guestName || isLoading}
              className="btn btn-primary flex-1"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add to Waitlist'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
